<template>
  <div>
    <Breadcrumbs
      :breadcrumbs="breadcrumbs"
    >
      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <va-dropdown
          v-if="hasWriteAppointmentsPermissions"
          class="custom-dropdown"
          fixed>
        <span slot="anchor">
          <slot>
            <div class="pl-3 pr-2 clickable">
              <span class="fa fa-ellipsis-v" style="font-size: 1.5rem;"></span>
            </div>
          </slot>
        </span>
          <div class="contact-dropdown__content pl-3 pr-2 pt-2 pb-2">
            <div v-if="hasWriteAppointmentsPermissions"
                 class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
                 @click.prevent="isUpdatingAppointment = true">
              <i class="fa fa-pencil"></i>&nbsp;
              <span class="pl-2">{{ $t('buttons.update') }}</span>
            </div>
            <div v-if="hasDeleteAppointmentPermissions"
                 class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
                 @click.prevent="isDeletingAppointment = true">
              <i class="fa fa-trash"></i>&nbsp;
              <span class="pl-2">{{ $t('buttons.delete') }}</span>
            </div>
          </div>

        </va-dropdown>
      </div>
    </Breadcrumbs>
    <section v-if="appointment">
      <ClientInfo
        class="mb-5 py-2"
        :appointment="appointment"
      />

      <AppointmentInfo
        class="mb-5 py-2"
        :appointment="appointment"
      />

      <AppointmentPaymentInfo
        class="mb-5 py-2"
        :appointment="appointment"
        @appointment-payment-updated="onAppointmentPaymentUpdated"
      />

      <AppointmentStatusHistoryTable
        class="mb-5 py-2"
        :appointment="appointment"
        @appointment-status-updated="onAppointmentStatusUpdated"
      />

      <AppointmentFilesTable
        class="mb-5 py-2"
        :appointment="appointment"
        :fileType="appointmentFileTypes.RESULT_FILE"
        :files="appointmentResultFiles"
        :title="$t('homelab.labels.result_files')"
        :editable="true"
        @appointment-files-updated="onAppointmentFilesUpdated"
      />

      <AppointmentFilesTable
        class="mb-5 py-2"
        :appointment="appointment"
        :fileType="appointmentFileTypes.PATIENT_FILE"
        :files="appointmentPatientFiles"
        :title="$t('homelab.labels.patient_files')"
        :editable="true"
        @appointment-files-updated="onAppointmentFilesUpdated"
      />

      <CreateOrUpdateAppointment
        v-if="isUpdatingAppointment"
        :appointment="appointment"
        :deletable="false"
        @appointment-updated="onAppointmentUpdated"
        @cancel="isUpdatingAppointment = false"
      />

      <ConfirmationModal v-if="isDeletingAppointment"
                         :body="$t('homelab.messages.confirm_delete', {name: appointment.name})"
                         @modal-canceled="isDeletingAppointment = false"
                         @modal-confirmed="confirmDeleteAppointment">
      </ConfirmationModal>
    </section>
  </div>
</template>
<script>
import {mapState} from 'vuex'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import ClientInfo from '@/components/homelab/appointments/details/ClientInfo'
import AppointmentInfo from '@/components/homelab/appointments/details/AppointmentInfo'
import {AppointmentService} from '@/api/HomelabService'
import AppointmentStatusHistoryTable from '@/components/homelab/appointments/details/AppointmentStatusHistoryTable'
import CreateOrUpdateAppointment from '@/components/homelab/appointments/CreateOrUpdateAppointment'
import AppointmentFilesTable from '@/components/homelab/appointments/details/AppointmentFilesTable'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import AppointmentPaymentInfo from '@/components/homelab/appointments/details/AppointmentPaymentInfo'
import IsFromEvolluMixin from '@/mixins/IsFromEvolluMixin'

export default {
  props: {
    component: {
      type: Object,
      required: true
    },
    serviceSlug: {
      type: String,
      required: true
    }
  },
  mixins: [IsFromEvolluMixin],
  components: {
    ClientInfo,
    AppointmentPaymentInfo,
    Breadcrumbs,
    AppointmentInfo,
    ConfirmationModal,
    AppointmentFilesTable,
    AppointmentStatusHistoryTable,
    CreateOrUpdateAppointment
  },
  mixins: [IsFromEvolluMixin],
  data: function () {
    return {
      isUpdatingAppointment: false,
      isDeletingAppointment: false,
      appointment: null
    }
  },
  computed: {
    ...mapState('homelab', [
      'appointmentFileTypes',
      'infoTypes',
      'accessTypes'
    ]),
    breadcrumbs: function () {
      let breadcrumbs = [
        {title: this.$t('menu.homelab'), disabled: true},
        {
          title: this.$t('homelab.appointments'),
          meta: {type: 'string', value: `/${this.serviceSlug}/appointments`}
        }
      ]

      if (this.appointment) {
        breadcrumbs.push({title: this.$t('homelab.appointment_details_title', {name: this.appointment.name})})
      }
      return breadcrumbs
    },
    homelabService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.homelab_server)
    },
    hasWriteAppointmentsPermissions: function () {
      return this.homelabService.user_permissions.some(permission => (permission.entity_id === null || permission.entity_id === this.appointment.entity.id) && permission.access_type > this.accessTypes.READ && permission.info_type === this.infoTypes.APPOINTMENTS)
    },
    hasDeleteAppointmentPermissions: function () {
      return (!this.homelabService.user_permissions.some(permission => (permission.entity_id === null || permission.entity_id === 1) && permission.access_type > this.accessTypes.WRITE && permission.info_type === this.infoTypes.APPOINTMENTS) && this.isFromEvollu)
    },
    appointmentResultFiles: function () {
      if (Array.isArray(this.appointment.files)) {
        return this.appointment.files.filter(appointmentFile => appointmentFile.file_type_id === this.appointmentFileTypes.RESULT_FILE)
      } else {
        return []
      }
    },
    appointmentPatientFiles: function () {
      if (Array.isArray(this.appointment.files)) {
        return this.appointment.files.filter(appointmentFile => appointmentFile.file_type_id === this.appointmentFileTypes.PATIENT_FILE)
      } else {
        return []
      }
    }
  },
  created: function () {
    this.$store.dispatch('homelab/INITIALIZE_MODULE')
    this.loadAppointment(this.$route.params.id)
  },
  methods: {
    onAppointmentUpdated: function (appointment) {
      this.appointment = appointment
    },
    onAppointmentStatusUpdated: function ({status, history}) {
      this.appointment.status = status
      this.appointment.status_history = history
    },
    onAppointmentFilesUpdated: function ({files}) {
      this.appointment.files = files
    },
    onAppointmentPaymentUpdated: function ({payment}) {
      this.appointment.payment = payment
    },
    loadAppointment: function (id) {
      AppointmentService.get(id)
        .then(appointment => {
          console.dir(appointment);
          this.appointment = appointment
        })
        .catch(errors => {
          console.log(errors)
          //console.log(`${this.serviceSlug}/homelab/appointments`)
          this.$router.push({name: `${this.serviceSlug}homelab/appointments`})
        })
    },
    confirmDeleteAppointment: function () {
      AppointmentService.destroy(this.appointment.entity.id, this.appointment.id)
        .then(response => {
          this.showToast(this.$t('homelab.messages.appointment_deleted'), this.$store.state.successToastOptions)
          this.$router.push({name: `${this.serviceSlug}homelab/appointments`})
        })
        .catch(errors => {
          console.log(errors)
          this.showToast(this.$t('homelab.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.isDeletingAppointment = false
        })
    }
  }
}
</script>
